/**
 * Die gemeinsame Lane: Richtung und Fortschritt (Produktentscheidung vom
 * 07.10.2026).
 *
 * Bis zum Umbau hatte jeder Spieler eine eigene Lane, und jede Einheit lief
 * darin in dieselbe Richtung: von `travelled = 0` zum Portal am Pfadende. Das
 * machte eine Regel moeglich, die an der wichtigsten Stelle der Simulation
 * steht:
 *
 *   > Der Fortschritt kann nur STEIGEN, nie sinken.
 *
 * Sie verhindert, dass ein Hero einen Creep endlos hin- und herzieht, ohne
 * dass der je naeher ans Portal kommt.
 *
 * Auf der gemeinsamen Lane laufen die beiden Armeen GEGENEINANDER. Fuer die
 * eine waechst `travelled`, fuer die andere faellt es - die Regel waere damit
 * fuer eine Seite falsch herum.
 *
 * Die Loesung ist dieses Modul. Es trennt zwei Dinge, die bisher dasselbe
 * waren:
 *
 *   travelled   WO auf dem Pfad die Einheit steht. Absolut, 0 bis Laenge.
 *               Fuer beide Seiten dieselbe Skala, damit es eine Wahrheit gibt.
 *
 *   fortschritt WIE WEIT sie auf ihrem Weg gekommen ist. Beginnt bei ihrem
 *               Spawn nahe 0 und waechst bis zur Laenge am Ziel-Anchor -
 *               fuer BEIDE Richtungen.
 *
 * Damit gilt "der Fortschritt kann nur steigen" unveraendert weiter. Die
 * Regel und ihre Begruendung bleiben stehen; nur die Groesse darunter
 * wechselt.
 */

import {
  ANCHOR_POSITION,
  CREEP_SPAWN_OFFSET,
  HERO_BOUNDS,
  LANE_HALF_WIDTH,
} from '@dueline/game-data';
import { clamp, type PlayerSide, type Vec2 } from '@dueline/shared';
import { begrenzeQuerZurLane, projectOntoLane } from './lane.js';

/**
 * Laufrichtung auf dem Pfad: +1 geht Richtung wachsendes `travelled`.
 */
export type LaneDirection = 1 | -1;

/**
 * In welche Richtung laeuft ein Creep, der das Anchor dieser Seite angreift?
 *
 * Das Blau-Anchor liegt bei `travelled = 0`, das Orange-Anchor am Pfadende.
 * Wer Orange angreift, laeuft also vorwaerts; wer Blau angreift, rueckwaerts.
 *
 * Der Parameter ist bewusst das ZIEL und nicht der Absender: Im Zustand steht
 * an jedem Creep `laneOwner` - der Spieler, auf dessen Anchor er zulaeuft.
 * Dieses Feld hat den Umbau unveraendert ueberlebt, und daraus laesst sich die
 * Richtung direkt ableiten, ohne ein zweites Feld zu pflegen.
 */
export function getAttackDirection(target: PlayerSide): LaneDirection {
  return target === 'orange' ? 1 : -1;
}

/**
 * Der zurueckgelegte Weg, richtungsnormiert.
 *
 * Null am eigenen Pfadende, `pathLength` am Ziel-Anchor - unabhaengig davon,
 * in welche Richtung die Einheit laeuft.
 */
export function getProgress(
  travelled: number,
  direction: LaneDirection,
  pathLength: number,
): number {
  return direction === 1 ? travelled : pathLength - travelled;
}

/**
 * Die Umkehrung: aus einem Fortschritt wieder die Stelle auf dem Pfad.
 *
 * Gebraucht ueberall dort, wo mit Fortschritt gerechnet und danach eine
 * Position gebraucht wird - zum Beispiel beim Marsch.
 */
export function getTravelledFromProgress(
  progress: number,
  direction: LaneDirection,
  pathLength: number,
): number {
  return direction === 1 ? progress : pathLength - progress;
}

/**
 * Wo eine Welle erscheint, die auf das Anchor dieser Seite zulaeuft.
 *
 * Immer auf der Seite des ABSENDERS, also am entgegengesetzten Ende. Der
 * Abstand zum eigenen Anchor ist fuer beide Seiten gleich - sonst haette eine
 * Seite einen kuerzeren Weg.
 */
export function getSpawnTravelled(
  target: PlayerSide,
  pathLength: number,
  offset: number = CREEP_SPAWN_OFFSET,
): number {
  return getTravelledFromProgress(offset, getAttackDirection(target), pathLength);
}

/**
 * Hat eine Einheit ihr Ziel-Anchor erreicht?
 *
 * Gemessen am Fortschritt und nicht an der Position: Ein Creep, der neben der
 * Lane noch aufschliesst, soll das Anchor nicht aus der Ferne treffen. Genau
 * das war schon in der alten Lane die Regel.
 */
export function hasReachedAnchor(progress: number, pathLength: number, tolerance: number): boolean {
  return progress >= pathLength - tolerance;
}

/**
 * Begrenzt eine Heldenposition auf das Spielfeld.
 *
 * Nur noch die Karte, seit dem 08.10.2026: Die Grenze nach vorn ist die
 * Frontline-Leash (GAME_DESIGN Abschnitt 5a) und haengt an der eigenen
 * Army. Vorher endete hier zusaetzlich die eigene Haelfte - wer seinen
 * Gegner zurueckdraengte, wurde trotzdem in der Mitte festgehalten.
 *
 * Nach hinten ist der Hero frei: Er darf sich jederzeit zum eigenen Anchor
 * zurueckziehen.
 */
export function clampToSideBounds(position: Vec2, side: PlayerSide): Vec2 {
  const bounds = HERO_BOUNDS[side];
  // Quer zur Lane folgt die Grenze seit dem 11.10.2026 der Kurve - siehe
  // begrenzeQuerZurLane.
  return begrenzeQuerZurLane({ x: clamp(position.x, bounds.minX, bounds.maxX), y: position.y });
}

/** Liegt die Position auf dem Spielfeld? */
export function isInsideSideBounds(position: Vec2, side: PlayerSide): boolean {
  const bounds = HERO_BOUNDS[side];
  return (
    position.x >= bounds.minX &&
    position.x <= bounds.maxX &&
    projectOntoLane(position).distance <= LANE_HALF_WIDTH
  );
}

/** Das Anchor, das diese Seite verteidigt. */
export function getAnchorPosition(side: PlayerSide): Vec2 {
  return ANCHOR_POSITION[side];
}
